import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface Mensagem {
  id: number;
  texto: string;
  autor: "eu" | "outro";
  created_at?: string;
}

interface MensagemBackend {
  id: number | string;
  mensagem: string;
  remetente_id: number | string;
  created_at?: string | null;
}

interface ConversaResponse {
  conversa_id?: number | null;
  meu_id?: number | string;
  mensagens?: MensagemBackend[];
  outro_usuario?: {
    id?: number | string | null;
    nome?: string | null;
    avatar?: string | null;
  } | null;
}

interface EventoMensagem {
  id?: number | string;
  mensagem?: string;
  remetente_id?: number | string;
  created_at?: string | null;
}

interface ReservaCabecalho {
  tutor?: string;
  anfitriao?: string;
  tipo_servico?: string;
  servico_nome?: string;
  data_inicio?: string;
  data_fim?: string;
  hora_inicio?: string;
  hora_fim?: string;
  avatar?: string;
}

interface GrupoMensagens {
  chave: string;
  rotulo: string;
  mensagens: Mensagem[];
}

function getCsrfToken(): string {
  const el = document.querySelector(
    'meta[name="csrf-token"]'
  ) as HTMLMetaElement | null;

  return el?.content ?? "";
}

function getAvatarUrl(path?: string | null) {
  if (!path) return null;

  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  if (path.startsWith("/storage/")) {
    return path;
  }

  if (path.startsWith("/")) {
    return `/storage${path}`;
  }

  return `/storage/${path}`;
}

function avatarTexto(nome: string) {
  return nome
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte.charAt(0).toUpperCase())
    .join("");
}

function normalizarMensagem(
  payload: unknown,
  meuId: number
): Mensagem | null {
  const evento = payload as
    | EventoMensagem
    | { mensagem?: EventoMensagem }
    | null
    | undefined;

  const msg =
    evento &&
    typeof evento === "object" &&
    "id" in evento &&
    evento.id !== undefined
      ? (evento as EventoMensagem)
      : evento &&
        typeof evento === "object" &&
        "mensagem" in evento &&
        typeof evento.mensagem === "object"
      ? (evento.mensagem as EventoMensagem)
      : null;

  if (!msg?.id || !msg?.mensagem) {
    return null;
  }

  return {
    id: Number(msg.id),
    texto: String(msg.mensagem),
    autor: Number(msg.remetente_id) === meuId ? "eu" : "outro",
    created_at: msg.created_at ?? undefined,
  };
}

function formatarDataBr(valor?: string | null) {
  if (!valor) return "";

  try {
    const data = new Date(valor);
    if (!Number.isNaN(data.getTime())) {
      return data.toLocaleDateString("pt-BR");
    }
  } catch {}

  try {
    const data = new Date(`${valor}T00:00:00`);
    if (!Number.isNaN(data.getTime())) {
      return data.toLocaleDateString("pt-BR");
    }
  } catch {}

  return valor;
}

function formatarHoraCurta(valor?: string | null) {
  if (!valor) return "";

  if (valor.length >= 5 && valor.includes(":")) {
    return valor.slice(0, 5);
  }

  try {
    const data = new Date(valor);
    if (!Number.isNaN(data.getTime())) {
      return data.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  } catch {}

  return valor;
}

function montarPeriodoReserva(info: ReservaCabecalho | null) {
  if (!info) return "";

  const inicio = formatarDataBr(info.data_inicio);
  const fim = formatarDataBr(info.data_fim);
  const horaInicio = formatarHoraCurta(info.hora_inicio);
  const horaFim = formatarHoraCurta(info.hora_fim);

  if (horaInicio && horaFim && inicio) {
    return `${inicio} • ${horaInicio} às ${horaFim}`;
  }

  if (inicio && fim && inicio !== fim) {
    return `${inicio} até ${fim}`;
  }

  if (inicio) {
    return inicio;
  }

  return "";
}

function normalizarDataMensagem(msg: Mensagem): Date {
  const valor = msg.created_at || "";

  if (valor) {
    const dt = new Date(valor);
    if (!Number.isNaN(dt.getTime())) return dt;
  }

  return new Date();
}

function formatarHoraMensagem(msg: Mensagem): string {
  const dt = normalizarDataMensagem(msg);

  return dt.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatarRotuloData(data: Date): string {
  const hoje = new Date();
  const ontem = new Date();
  ontem.setDate(hoje.getDate() - 1);

  const dataBase = new Date(data);
  dataBase.setHours(0, 0, 0, 0);

  const hojeBase = new Date(hoje);
  hojeBase.setHours(0, 0, 0, 0);

  const ontemBase = new Date(ontem);
  ontemBase.setHours(0, 0, 0, 0);

  if (dataBase.getTime() === hojeBase.getTime()) return "Hoje";
  if (dataBase.getTime() === ontemBase.getTime()) return "Ontem";

  return data.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function agruparMensagensPorData(mensagens: Mensagem[]): GrupoMensagens[] {
  const grupos = new Map<string, GrupoMensagens>();

  mensagens.forEach((msg) => {
    const data = normalizarDataMensagem(msg);

    const chave = [
      data.getFullYear(),
      String(data.getMonth() + 1).padStart(2, "0"),
      String(data.getDate()).padStart(2, "0"),
    ].join("-");

    if (!grupos.has(chave)) {
      grupos.set(chave, {
        chave,
        rotulo: formatarRotuloData(data),
        mensagens: [],
      });
    }

    grupos.get(chave)!.mensagens.push(msg);
  });

  return Array.from(grupos.values()).sort((a, b) =>
    a.chave.localeCompare(b.chave)
  );
}

export default function ChatPage() {
  const [searchParams] = useSearchParams();

  const reservaId = searchParams.get("reserva") || "";
  const userId = searchParams.get("user") || "";

  const [conversaId, setConversaId] = useState<number | null>(null);
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState("");
  const [novaMensagem, setNovaMensagem] = useState("");
  const [digitandoNome, setDigitandoNome] = useState("");
  const [visualizado, setVisualizado] = useState(false);
  const [reservaInfo, setReservaInfo] = useState<ReservaCabecalho | null>(null);
  const [outroUsuario, setOutroUsuario] = useState<ConversaResponse["outro_usuario"]>(null);

  const mensagensRef = useRef<HTMLDivElement | null>(null);
  const meuIdRef = useRef<number>(0);
  const typingTimeoutRef = useRef<number | null>(null);
  const typingDebounceRef = useRef<number | null>(null);

  const csrfToken = useMemo(() => getCsrfToken(), []);

  const mensagensAgrupadas = useMemo(() => {
    return agruparMensagensPorData(mensagens);
  }, [mensagens]);

  const limparTimers = useCallback(() => {
    if (typingTimeoutRef.current) {
      window.clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    if (typingDebounceRef.current) {
      window.clearTimeout(typingDebounceRef.current);
      typingDebounceRef.current = null;
    }
  }, []);

  const adicionarMensagemSeNova = useCallback((mensagem: Mensagem) => {
    setMensagens((prev) => {
      const jaExiste = prev.some((m) => m.id === mensagem.id);
      if (jaExiste) return prev;
      return [...prev, mensagem];
    });
  }, []);

  const carregarCabecalhoReserva = useCallback(async () => {
    if (!reservaId) {
      setReservaInfo(null);
      return;
    }

    try {
      const resp = await fetch(`/reserva/${reservaId}/detalhes-json`, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });

      if (!resp.ok) {
        setReservaInfo(null);
        return;
      }

      const data = await resp.json();

      setReservaInfo({
        tutor: data.tutor || data.nome_tutor || data.nome_usuario || "",
        anfitriao: data.anfitriao || data.nome_anfitriao || "",
        tipo_servico: data.tipo_servico || "",
        servico_nome: data.servico_nome || data.servico || data.tipo_servico || "",
        data_inicio: data.data_inicio || "",
        data_fim: data.data_fim || "",
        hora_inicio: data.hora_inicio || "",
        hora_fim: data.hora_fim || "",
        avatar:
          data.avatar ||
          data.tutor_avatar ||
          data.foto_perfil ||
          data.usuario_avatar ||
          "",
      });
    } catch (error) {
      console.error("Erro ao carregar cabeçalho da reserva:", error);
      setReservaInfo(null);
    }
  }, [reservaId]);

  const carregarMensagens = useCallback(
    async (mostrarLoading = false) => {
      if (!userId) {
        setErro("Usuário da conversa não informado.");
        setCarregando(false);
        return;
      }

      try {
        if (mostrarLoading) {
          setCarregando(true);
        }

        setErro("");

        const conversaResp = await fetch(`/chat/get-conversa/${userId}`, {
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          credentials: "same-origin",
        });

        if (!conversaResp.ok) {
          throw new Error("Erro ao carregar conversa.");
        }

        const conversaData: ConversaResponse = await conversaResp.json();

        const idConversa =
          typeof conversaData.conversa_id === "number"
            ? conversaData.conversa_id
            : null;

        setConversaId(idConversa);

        const meuId = Number(conversaData.meu_id ?? 0);
        meuIdRef.current = meuId;
        setOutroUsuario(conversaData.outro_usuario ?? null);

        const lista: Mensagem[] = (conversaData.mensagens || []).map((msg) => ({
          id: Number(msg.id),
          texto: String(msg.mensagem),
          autor: Number(msg.remetente_id) === meuId ? "eu" : "outro",
          created_at: msg.created_at ?? undefined,
        }));

        setMensagens(lista);
      } catch (error) {
        console.error(error);
        setErro("Não foi possível carregar as mensagens.");
      } finally {
        setCarregando(false);
      }
    },
    [userId]
  );

  const marcarVisualizado = useCallback(async () => {
    if (!conversaId) return;

    try {
      await fetch(`/chat/conversa/${conversaId}/visualizado`, {
        method: "POST",
        credentials: "include",
        headers: {
          "X-CSRF-TOKEN": csrfToken,
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
      });
    } catch (error) {
      console.error("Erro ao marcar como visualizado:", error);
    }
  }, [conversaId, csrfToken]);

  const enviarDigitando = useCallback(async () => {
    if (!conversaId) return;
    if (!novaMensagem.trim()) return;

    try {
      await fetch("/chat/digitando", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-TOKEN": csrfToken,
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
        body: JSON.stringify({
          conversa_id: conversaId,
        }),
      });
    } catch (error) {
      console.error("Erro ao enviar status de digitação:", error);
    }
  }, [conversaId, csrfToken, novaMensagem]);

  const enviarMensagem = useCallback(async () => {
    if (!novaMensagem.trim() || !conversaId || enviando) return;

    const texto = novaMensagem.trim();

    try {
      setEnviando(true);
      setErro("");
      setVisualizado(false);

      const resp = await fetch("/chat/enviar", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-TOKEN": csrfToken,
        },
        credentials: "same-origin",
        body: JSON.stringify({
          conversa_id: conversaId,
          mensagem: texto,
        }),
      });

      if (!resp.ok) {
        throw new Error("Erro ao enviar mensagem.");
      }

      const data = await resp.json();
      const mensagemNormalizada = normalizarMensagem(data, meuIdRef.current);

      if (!mensagemNormalizada) {
        throw new Error("Resposta do backend inválida.");
      }

      adicionarMensagemSeNova({
        ...mensagemNormalizada,
        autor: "eu",
      });

      setNovaMensagem("");
      setDigitandoNome("");
    } catch (error) {
      console.error(error);
      setErro("Não foi possível enviar a mensagem.");
    } finally {
      setEnviando(false);
    }
  }, [
    novaMensagem,
    conversaId,
    enviando,
    csrfToken,
    adicionarMensagemSeNova,
  ]);

  useEffect(() => {
    setMensagens([]);
    setConversaId(null);
    setErro("");
    setCarregando(true);
    setDigitandoNome("");
    setVisualizado(false);
    setOutroUsuario(null);
    limparTimers();

    carregarMensagens(true);
    carregarCabecalhoReserva();

    return () => {
      limparTimers();
    };
  }, [carregarMensagens, carregarCabecalhoReserva, limparTimers]);

  useEffect(() => {
    if (!conversaId) return;

    const echo = (window as any).Echo;
    if (!echo) return;

    const channelName = `chat.${conversaId}`;

    try {
      echo.leave(channelName);
    } catch {}

    const channel = echo.private(channelName);

    channel.listen(".NovaMensagemEnviada", (event: unknown) => {
      const mensagemNormalizada = normalizarMensagem(event, meuIdRef.current);

      if (!mensagemNormalizada) return;

      adicionarMensagemSeNova(mensagemNormalizada);

      if (mensagemNormalizada.autor === "outro") {
        setDigitandoNome("");
        marcarVisualizado();
      }
    });

    channel.listen(".ChatDigitando", (event: any) => {
      if (!event?.user_id) return;
      if (Number(event.user_id) === meuIdRef.current) return;

      setDigitandoNome(String(event.nome || "Usuário"));

      if (typingTimeoutRef.current) {
        window.clearTimeout(typingTimeoutRef.current);
      }

      typingTimeoutRef.current = window.setTimeout(() => {
        setDigitandoNome("");
      }, 2000);
    });

    channel.listen(".ChatVisualizado", (event: any) => {
      if (!event?.user_id) return;
      if (Number(event.user_id) === meuIdRef.current) return;

      setVisualizado(true);
    });

    return () => {
      try {
        echo.leave(channelName);
      } catch {}

      limparTimers();
    };
  }, [
    conversaId,
    adicionarMensagemSeNova,
    marcarVisualizado,
    limparTimers,
  ]);

  useEffect(() => {
    mensagensRef.current?.scrollTo({
      top: mensagensRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [mensagens]);

  const headerName =
    reservaInfo?.anfitriao ||
    outroUsuario?.nome ||
    reservaInfo?.tutor ||
    `Chat da reserva #${reservaId || "—"}`;

  const headerAvatar =
    getAvatarUrl(reservaInfo?.avatar) ||
    getAvatarUrl(outroUsuario?.avatar || undefined);

  const headerSubtitle =
    [
      reservaInfo?.servico_nome || reservaInfo?.tipo_servico || "",
      montarPeriodoReserva(reservaInfo),
      reservaId ? `Reserva #${reservaId}` : "",
    ]
      .filter(Boolean)
      .join(" • ") || "Conversa com o anfitrião";

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f6faf6] via-[#eef4ee] to-[#e8f0e8]">
      <Header />

      <div className="mx-auto max-w-3xl px-4 py-6">
        <Card className="flex h-[80vh] flex-col rounded-3xl border-0 bg-white shadow-sm">
          <CardContent className="flex h-full flex-col p-4">
            <div className="mb-4 border-b pb-3">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-[#FE5668] text-sm font-bold text-white">
                  {headerAvatar ? (
                    <img
                      src={headerAvatar}
                      alt={headerName}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    avatarTexto(headerName || "U")
                  )}
                </div>

                <div className="min-w-0">
                  <h1 className="truncate text-lg font-bold text-gray-900">
                    {headerName}
                  </h1>

                  <p className="mt-1 text-sm text-gray-500">
                    {headerSubtitle}
                  </p>
                </div>
              </div>
            </div>

            {erro && (
              <div className="mb-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {erro}
              </div>
            )}

            <div ref={mensagensRef} className="flex-1 overflow-y-auto pr-2">
              {carregando && (
                <div className="text-sm text-gray-500">
                  Carregando mensagens...
                </div>
              )}

              {!carregando && mensagens.length === 0 && (
                <div className="text-sm text-gray-500">
                  Nenhuma mensagem nesta conversa ainda.
                </div>
              )}

              {!carregando && mensagens.length > 0 && (
                <div className="space-y-3">
                  {mensagensAgrupadas.map((grupo) => (
                    <div key={grupo.chave} className="space-y-3">
                      <div className="sticky top-2 z-10 flex justify-center">
                        <span className="rounded-full border border-gray-200 bg-white/90 px-3 py-1 text-xs font-medium text-gray-500 shadow-sm backdrop-blur">
                          {grupo.rotulo}
                        </span>
                      </div>

                      {grupo.mensagens.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${
                            msg.autor === "eu" ? "justify-end" : "justify-start"
                          }`}
                        >
                          <div
                            className={`max-w-[75%] rounded-2xl px-4 py-2 shadow ${
                              msg.autor === "eu"
                                ? "rounded-br-md bg-[#DCF8C6] text-gray-800"
                                : "rounded-bl-md border border-gray-200 bg-gray-100 text-gray-800"
                            }`}
                          >
                            <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                              {msg.texto}
                            </div>

                            <div
                              className={`mt-1 text-right text-[11px] leading-none ${
                                msg.autor === "eu"
                                  ? "text-gray-500"
                                  : "text-gray-400"
                              }`}
                            >
                              {formatarHoraMensagem(msg)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-2 min-h-[20px] text-xs text-gray-500">
              {digitandoNome
                ? `${digitandoNome} está digitando...`
                : visualizado
                ? "Visualizado"
                : ""}
            </div>

            <div className="mt-2 flex gap-2">
              <input
                type="text"
                placeholder="Digite sua mensagem..."
                className="flex-1 rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-green-500"
                value={novaMensagem}
                onChange={(e) => {
                  const valor = e.target.value;
                  setNovaMensagem(valor);

                  if (typingDebounceRef.current) {
                    window.clearTimeout(typingDebounceRef.current);
                  }

                  typingDebounceRef.current = window.setTimeout(() => {
                    if (valor.trim()) {
                      enviarDigitando();
                    }
                  }, 300);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    enviarMensagem();
                  }
                }}
                disabled={!conversaId || enviando}
                autoComplete="off"
              />

              <Button
                onClick={enviarMensagem}
                disabled={!conversaId || enviando}
                className="rounded-2xl bg-green-600 px-5 text-white"
                type="button"
              >
                {enviando ? "Enviando..." : "Enviar"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
